import React, { ChangeEvent, ReactNode, useEffect, useState, useRef } from 'react';
import './StopDetails.scss';
import { useSearchParams, Link } from 'react-router-dom';
import Grid from '@mui/material/Unstable_Grid2';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { CellContext, ColumnDef, ExpandedState } from '@tanstack/react-table';
import { getLocationShipmentTrackingDetails } from 'src/api/shipmentTracking';
import { formatDate } from 'src/util/formatters';
import { getLocationById } from 'src/api/locations';
import { CustomerInfo, DateFormats, FmtpError, Permissions, ShippingPortalLocation } from '@lanterds/types';
import { AddressPicker } from 'src/components/common/AddressPicker/AddressPicker';
import dayjs, { Dayjs } from 'dayjs';
import { allCustomersDatum, allCustomersValue } from 'src/util/constants';
import { getCustomersByPermission } from 'src/api/customer';
import { useErrorHandler } from 'react-error-boundary';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { isAuthorized } from 'src/util/Permissions';
import { getUserEmailFromToken, sortByDate } from 'src/util/helpers';
import duration from 'dayjs/plugin/duration';
import EmptyTreeNode from 'src/components/common/FmtpTable/EmtpyTreeNode';
import { useSnackbar, SnackbarKey } from 'notistack';

dayjs.extend(duration);

interface FieldErrors {
    startDateError?: string;
    endDateError?: string;
    dateDropdownError?: string;
    endpointError?: string;
}

enum ERROR_MESSAGES {
    START_DATE_MISSING = 'Please select a Start Date',
    END_DATE_MISSING = 'Please select an End Date',
    DATE_DROPDOWN_MISSING = 'Please select a Date',
    START_DATE_GREATER_THAN_END_DATE = 'Start Date cannot be after the End Date',
    ENDPOINT_ERROR = 'Please select a Location.',
}

const TODAY = { value: dayjs().format(DateFormats.IsoDate), label: 'Today' };
const YESTERDAY = {
    value: dayjs().subtract(1, 'day').format(DateFormats.IsoDate),
    label: 'Yesterday',
};

const dateOptions = [TODAY, YESTERDAY];

export default function StopDetails() {
    const handleError = useErrorHandler();
    const [searchParams, setSearchParams] = useSearchParams();
    const locationId = searchParams.get('locationId');
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(locationId);
    const [selectedRows, setSelectedRows] = useState<{ [rowId: string]: boolean }>({});
    const useDateRangeParam = searchParams.get('useDateRange');
    const [useDateRange, setUseDateRange] = useState(useDateRangeParam === 'true');
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    const [selectedDates, setSelectedDates] = useState<(Dayjs | null)[]>([
        startDate ? dayjs(startDate) : dayjs(),
        endDate ? dayjs(endDate) : dayjs(),
    ]);
    const [tableData, setTableData] = useState<any[]>([]);
    const [data, setData] = useState<any[]>([]);
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [isLoadingParcels, setIsLoadingParcels] = useState<boolean>(false);
    const [selectedEndpoint, setSelectedEndpoint] = useState<ShippingPortalLocation | null>(null);
    const [customers, setCustomers] = useState<CustomerInfo[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<{ id: string; label: string } | null>(null);
    const [customersAreLoading, setCustomersAreLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const snackbarKey = useRef<SnackbarKey | undefined>(undefined);

    useEffect(() => {
        return () => {
            if (snackbarKey.current) {
                closeSnackbar(snackbarKey.current);
                snackbarKey.current = undefined;
            }
        };
    }, [closeSnackbar]);

    const userEmail = getUserEmailFromToken();
    const isParcelSearchAuthorized = isAuthorized(userEmail, Permissions.parcelSearch);

    function TreeCell({ row }: { row: any }) {
        return (
            <Box
                className={`tree-branch level-${row.depth}`}
                sx={{ pl: row.depth * 2, display: 'flex', alignItems: 'center', position: 'relative' }}
            >
                test
            </Box>
        );
    }

    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'categoryName',
            header: 'Arrival',
            sortingFn: 'alphanumeric',
            meta: {
                isExpandable: true,
                innerHtml: ({ getValue, row }) => {
                    if (getValue()) {
                        return getValue();
                    } else {
                        return <EmptyTreeNode row={row} />;
                    }
                },
            },
        },
        {
            accessorKey: 'parcelCode',
            header: 'Parcels',
            sortingFn: 'alphanumeric',
            size: 150,
            meta: {
                innerHtml: ({ row }: CellContext<any, unknown>) => {
                    const { subRows, parcelCode, deliveryScanUTC, loadScanUTC, receiptScanUTC } = row.original;
                    const queryDate = receiptScanUTC ?? loadScanUTC ?? deliveryScanUTC;

                    const canCreateUrl =
                        !!parcelCode &&
                        parcelCode.toUpperCase() !== 'NULL' &&
                        !!queryDate &&
                        !!selectedEndpoint?.fmtpCustomerId &&
                        selectedEndpoint.fmtpCustomerId.toUpperCase() !== 'NULL' &&
                        selectedEndpoint.fmtpCustomerId.toUpperCase() !== 'UNKNOWN';

                    let detailUrl = '';

                    if (selectedEndpoint) {
                        detailUrl = `/parcel/${parcelCode}/${queryDate}/${selectedEndpoint.fmtpCustomerId}`;
                    }

                    return (
                        <>
                            {isParcelSearchAuthorized && !subRows && canCreateUrl && detailUrl ? (
                                <Box className="parcel-code-cell">
                                    <Link to={detailUrl} target="_blank" rel="noreferrer">
                                        {parcelCode}
                                    </Link>
                                </Box>
                            ) : (
                                parcelCode
                            )}
                        </>
                    );
                },
            },
        },
        {
            accessorKey: 'possessionScan',
            sortingFn: 'alphanumeric',
            header: 'Possession Date',
            meta: {
                innerHtml: ({ getValue }: CellContext<any, unknown>) => {
                    {
                        return getValue()
                            ? (dayjs(getValue() as string)
                                  // eslint-disable-next-line indent
                                  .utc()
                                  // eslint-disable-next-line indent
                                  .format(DateFormats.ShortDateTime) as ReactNode)
                            : '';
                    }
                },
            },
            size: 80,
        },
        {
            accessorKey: 'weight',
            sortingFn: 'alphanumeric',
            header: 'Weight',
            size: 50,
        },
        {
            accessorKey: 'referenceNumber',
            sortingFn: 'alphanumeric',
            header: 'Ref/Manifest',
            size: 80,
        },
        {
            accessorKey: 'partNumber',
            sortingFn: 'alphanumeric',
            header: 'Part Number',
            size: 80,
        },
        {
            accessorKey: 'status',
            sortingFn: 'alphanumeric',
            header: 'Status',
            size: 100,
        },
    ];

    useEffect(() => {
        setSearchParams({
            ...searchParams,
            ...(selectedLocationId ? { locationId: selectedLocationId } : {}),
            ...(selectedDates ? { startDate: dayjs(selectedDates[0]).format(DateFormats.IsoDate) } : {}),
            ...(selectedDates ? { endDate: dayjs(selectedDates[1]).format(DateFormats.IsoDate) } : {}),
            useDateRange: useDateRange.toString(),
        });

        if (selectedLocationId) {
            getLocationById(selectedLocationId)
                .then((data) => {
                    setSelectedEndpoint(data);
                })
                .catch((err) => {
                    //TODO handle error;
                    console.error(err);
                })
                .finally(() => {
                    //setIsLoadingParcels(false);
                });
        }
    }, [selectedLocationId, selectedDates, useDateRange]);

    useEffect(() => {
        let sortedData = [...data];
        sortedData = sortByDate(sortByDate(sortedData, 'deliveryScanLocal', 'ASC'), 'commitDate', 'ASC');
        const deliveredItems: any[] = [];
        const inPossessionItems: any[] = [];

        sortedData.forEach((d) => {
            if (d.deliveryScanLocal) {
                deliveredItems.push(d);
            } else {
                inPossessionItems.push(d);
            }
        });

        const tableData = [];

        if (deliveredItems.length) {
            // Split these out by route

            const routeParcelMap: Record<string, any[]> = {};
            let routeItem = '';

            deliveredItems.forEach((item, index) => {
                if (index === 0) {
                    // Create unique Id with name and scan to prevent overwriting items with same name
                    routeItem = `${item.routeName}${item.deliveryScanLocal}`;
                    routeParcelMap[routeItem] = [item];
                } else {
                    const routeItemScan = dayjs(routeParcelMap[routeItem][0].deliveryScanLocal);
                    const currentItemScan = dayjs(item.deliveryScanLocal);

                    // Calculate the difference in milliseconds
                    const diffInMilliseconds = currentItemScan.diff(routeItemScan);

                    // Convert milliseconds to hours
                    const diffInHours = dayjs.duration(diffInMilliseconds).asHours();

                    if (diffInHours <= 3) {
                        routeParcelMap[routeItem].push(item);
                    } else {
                        routeItem = `${item.routeName}${item.deliveryScanLocal}`;
                        routeParcelMap[routeItem] = [item];
                    }
                }
            });

            for (const item in routeParcelMap) {
                tableData.push({
                    subRows: sortByDate(routeParcelMap[item], 'possessionScan', 'ASC'),
                    categoryName: `Arrived at ${formatDate(routeParcelMap[item][0]?.deliveryScanLocal)}`,
                    parcelCode: `${routeParcelMap[item].length} Parcels`,
                });
            }
        }

        if (inPossessionItems.length) {
            tableData.push({
                subRows: sortByDate(inPossessionItems, 'possessionScan', 'ASC'),
                categoryName: 'In Possession',
                parcelCode: `${inPossessionItems.length} Parcels`,
            });
        }

        setTableData(tableData);
        // Open all items by default
        setExpandedRows(
            tableData.reduce((prev, curr, i) => {
                return { ...prev, [i]: true };
            }, {}),
        );
    }, [data]);

    const onExpandedChange = async (row: () => ExpandedState) => {
        const rowNumber = Number(Object.keys(row())[0]);
        const newExpandedList = { ...expandedRows };

        if (!expandedRows[rowNumber]) {
            newExpandedList[rowNumber] = true;
            setExpandedRows(newExpandedList);
        } else {
            newExpandedList[rowNumber] = false;
            setExpandedRows(newExpandedList);
        }
    };

    const handleDateRangeChange = (value: Dayjs | null, name: string) => {
        if (name === 'startDate') {
            setSelectedDates([value, selectedDates[1]]);
        } else {
            setSelectedDates([selectedDates[0], value]);
        }
    };

    const handleDateChange = (
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | SelectChangeEvent,
    ) => {
        setSelectedDates([dayjs(e.target.value), selectedDates[1]]);
    };

    const handleAddressPickerChange = (locations: ShippingPortalLocation | ShippingPortalLocation[] | null) => {
        const id = locations ? (locations as ShippingPortalLocation).id : null;
        setSelectedLocationId(id);
        setSelectedEndpoint(locations as ShippingPortalLocation | null);
        if (!id) {
            searchParams.delete('locationId');
        }
    };

    useEffect(() => {
        setCustomersAreLoading(true);
        // TODO replace with own customer stuff
        getCustomersByPermission(Permissions.shipmentTracking).then((data) => {
            data.sort((a: CustomerInfo, b: CustomerInfo) =>
                a.customerName.toLocaleLowerCase().localeCompare(b.customerName.toLocaleLowerCase()),
            );

            if (data.length > 1) {
                setCustomers([allCustomersDatum, ...data]);
                setSelectedCustomer(allCustomersValue);
            } else {
                setCustomers([data]);
            }

            setCustomersAreLoading(false);
        });
    }, []);

    const onChangeCustomer = (value: { label: string; id: string } | null) => {
        if (value) {
            setSelectedCustomer(value);
        } else {
            setSelectedCustomer(allCustomersValue);
        }
    };

    const getCustomerFilter = () => {
        if (!selectedCustomer) return null;
        if (selectedCustomer.id === allCustomersValue.id) return null;
        return selectedCustomer;
    };

    const handleReset = () => {
        setUseDateRange(false);
        // Set date dropdown to Today
        setSelectedDates([dayjs(dateOptions[1].value), selectedDates[1]]);
        setSelectedCustomer(allCustomersValue);
        setSelectedLocationId(null);
        setSelectedEndpoint(null);
        setTableData([]);
        setFieldErrors({});
        setData([]);
    };

    const search = () => {
        const formIsValid = validateForm();

        if (selectedLocationId && selectedDates && formIsValid) {
            setData([]);
            setIsLoadingParcels(true);
            closeSnackbar();

            snackbarKey.current = enqueueSnackbar('Fetching shipments...', {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });

            getLocationShipmentTrackingDetails(
                selectedLocationId,
                dayjs(selectedDates[0]).toDate(),
                useDateRange ? dayjs(selectedDates[1]).toDate() : null,
            )
                .then((data) => {
                    setData(data);
                })
                .catch((err) => {
                    console.error(err);
                    handleError(
                        new FmtpError({
                            message: 'An error occurred while getting shipment tracking details.',
                            errorCode: 'SHU011',
                        }),
                    );
                })
                .finally(() => {
                    setIsLoadingParcels(false);
                    closeSnackbar(snackbarKey.current);
                });
        }
    };

    const handleUseDateRange = (_e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setUseDateRange(checked);
        setSelectedDates([dayjs(dateOptions[1].value), dayjs(dateOptions[1].value)]);
    };

    const validateForm = () => {
        let isValid = true;
        const newFieldErrors: FieldErrors = {};

        if (useDateRange) {
            const startDate = selectedDates[0];
            const endDate = selectedDates[1];

            if (!startDate || !startDate.isValid()) {
                newFieldErrors.startDateError = ERROR_MESSAGES.START_DATE_MISSING;
            }

            if (!endDate || !endDate.isValid()) {
                newFieldErrors.endDateError = ERROR_MESSAGES.END_DATE_MISSING;
            }

            if (startDate?.isValid() && endDate?.isValid()) {
                if (endDate.isBefore(startDate)) {
                    newFieldErrors.startDateError = ERROR_MESSAGES.START_DATE_GREATER_THAN_END_DATE;
                }
            }
        } else {
            const dateDropdownSelection = selectedDates[0];
            if (!dateDropdownSelection) {
                newFieldErrors.dateDropdownError = ERROR_MESSAGES.DATE_DROPDOWN_MISSING;
            }
        }

        if (!selectedEndpoint) {
            newFieldErrors.endpointError = ERROR_MESSAGES.ENDPOINT_ERROR;
        }

        if (Object.keys(newFieldErrors).length) {
            setFieldErrors(newFieldErrors);
            isValid = false;
        }

        if (isValid) {
            setFieldErrors({});
        }

        return isValid;
    };

    const handleAddressPickerError = () => {
        const addressPickerError = {
            nameError: fieldErrors.endpointError,
            codeError: fieldErrors.endpointError,
            addressError: fieldErrors.endpointError,
        };
        return fieldErrors.endpointError ? addressPickerError : {};
    };

    return (
        <Box id="stop-details-container" className="auto-fill-height">
            <Card id="stop-details-form" className="auto-fill-height">
                <CardContent className="auto-fill-height">
                    <Grid xs={12} container spacing={2}>
                        <Grid xl={4} lg={5} md={12} sm={12} xs={12} container spacing={2}>
                            <Grid xs={12} container spacing={2}>
                                <Grid xs={12}>
                                    <FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={useDateRange}
                                                    onChange={handleUseDateRange}
                                                    disableRipple
                                                    sx={{ paddingTop: 0, paddingBottom: 0, margin: 0 }}
                                                />
                                            }
                                            label="Use Date Range"
                                            checked={useDateRange}
                                        />
                                    </FormControl>
                                </Grid>
                                {useDateRange ? (
                                    <Grid xs={12} container spacing={2}>
                                        <Grid md={6} sm={12} xs={12}>
                                            <FormControl fullWidth>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    {/* DatePicker is a special case where we define the label in the slotProps and not as an InputLabel */}
                                                    <DatePicker
                                                        desktopModeMediaQuery="@media (min-width: 400px)"
                                                        name="startDate"
                                                        value={selectedDates[0] ? selectedDates[0] : null}
                                                        onChange={(value) => handleDateRangeChange(value, 'startDate')}
                                                        slotProps={{
                                                            field: { clearable: true },
                                                            actionBar: {
                                                                actions: ['clear', 'today'],
                                                                sx: { justifyContent: 'space-between' },
                                                            },
                                                            textField: {
                                                                id: 'date',
                                                                variant: 'outlined',
                                                                placeholder: 'MM/DD/YYYY',
                                                                label: 'Start Date',
                                                                InputProps: {
                                                                    sx: { borderRadius: '10px' },
                                                                    fullWidth: true,
                                                                },
                                                                error: !!fieldErrors?.startDateError,
                                                                helperText: fieldErrors?.startDateError,
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </FormControl>
                                        </Grid>
                                        <Grid md={6} sm={12} xs={12}>
                                            <FormControl fullWidth>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    {/* DatePicker is a special case where we define the label in the slotProps and not as an InputLabel */}
                                                    <DatePicker
                                                        desktopModeMediaQuery="@media (min-width: 400px)"
                                                        name="endDate"
                                                        value={selectedDates[1] ? selectedDates[1] : null}
                                                        onChange={(value) => handleDateRangeChange(value, 'endDate')}
                                                        slotProps={{
                                                            field: { clearable: true },
                                                            actionBar: {
                                                                actions: ['clear', 'today'],
                                                                sx: { justifyContent: 'space-between' },
                                                            },
                                                            textField: {
                                                                id: 'date',
                                                                variant: 'outlined',
                                                                placeholder: 'MM/DD/YYYY',
                                                                label: 'End Date',
                                                                InputProps: {
                                                                    sx: { borderRadius: '10px' },
                                                                    fullWidth: true,
                                                                },
                                                                error: !!fieldErrors?.endDateError,
                                                                helperText: fieldErrors?.endDateError,
                                                            },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <Grid xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel id="date-select-label">Date</InputLabel>
                                            <Select
                                                labelId="date-select-label"
                                                name="date"
                                                id="date"
                                                value={selectedDates[0]?.format(DateFormats.IsoDate)}
                                                variant="outlined"
                                                label="Date"
                                                onChange={handleDateChange}
                                                error={!!fieldErrors?.dateDropdownError}
                                            >
                                                {dateOptions.map((option) => {
                                                    return (
                                                        <MenuItem key={option.value} disableRipple value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                            <FormHelperText
                                                sx={{
                                                    display: `${fieldErrors?.dateDropdownError ? 'block' : 'none'}`,
                                                    color: 'red',
                                                }}
                                            >
                                                {fieldErrors?.dateDropdownError}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>

                            <Grid xs={12}>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        id="customerId"
                                        options={customers.map((customer) => ({
                                            label: customer.customerName,
                                            id: customer.customerId,
                                        }))}
                                        loading={customersAreLoading}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label="Customer"
                                                name="customerId"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {customersAreLoading ? (
                                                                <CircularProgress color="inherit" size={20} />
                                                            ) : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                InputLabelProps={{ ...params.InputLabelProps }}
                                            />
                                        )}
                                        value={selectedCustomer}
                                        onChange={(event, value) => onChangeCustomer(value)}
                                        disabled={customersAreLoading}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid xl={5} lg={4} md={12} sm={12} xs={12} container spacing={2}>
                            <Grid xs={12}>
                                <Typography>Endpoint</Typography>
                            </Grid>
                            <AddressPicker
                                name="selectedLocation"
                                onChangeInput={handleAddressPickerChange}
                                context={{ searchType: 'shipmentTracking' }}
                                enableAutoPopulate={false}
                                locations={selectedEndpoint ? [selectedEndpoint] : []}
                                splitNameAndCodeFields={true}
                                customer={getCustomerFilter()}
                                fieldErrors={handleAddressPickerError()}
                            />
                        </Grid>
                        <Grid xl={3} lg={3} md={12} sm={12} xs={12} container spacing={2} className="buttons">
                            <CardActions>
                                <Button
                                    variant="outlined"
                                    className="clear-button"
                                    onClick={handleReset}
                                    sx={{ height: '56px', width: '100px' }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="contained"
                                    className="submit-button"
                                    onClick={search}
                                    sx={{ height: '56px', width: '100px' }}
                                >
                                    Search
                                </Button>
                            </CardActions>
                        </Grid>
                    </Grid>
                    {selectedLocationId && tableData.length > 0 && (
                        <FmtpTable
                            data={tableData}
                            columns={columns}
                            setSelectedRows={setSelectedRows}
                            selectedRows={selectedRows}
                            enableSorting={true}
                            isResizable={true}
                            enableFilters={false}
                            enableExpanding={true}
                            getSubRows={(row: any) => row.subRows}
                            onExpandedChange={onExpandedChange}
                            expanded={expandedRows}
                            showLoading={true}
                            isFetching={isLoadingParcels || customersAreLoading}
                            loadingIndicatorMessage={'Loading...'}
                            showTable={tableData.length > 0}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
